// Offline-first sync. Grades and other writes land in IndexedDB first; this engine pushes the
// outboxes (reviews, dirty cards, lesson rows, and the undone reviews in kv `reviewVoids`) and
// pulls changes when it can. It never blocks the UI: callers fire `request()` and
// carry on. Triggers: app start, `online`, page becoming visible (at most once a minute unless
// something waits to be sent), the end of each session, and queued writes (debounced).
//
// D1 budget: a sync is one POST /api/sync (plus pages of a full pull); the dictionary feed is
// read only when the pull's `dictVersion` differs from the last complete dictionary pull.
import type { CardRow, LessonProgressRow, ReviewRow, SyncPull, SyncPush } from '@valyrio/engine/api';
import { ApiError, type ApiClient } from './api';
import { kvGet, kvSet, toCardRow, type CardRecord, type OutboxWrite, type ReviewVoid, type ValyrioDB } from './db';
import { emptyOverlay, mergeChanges, type DictOverlay } from './dictionary';
import { cardKey } from './fsrs';

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'auth';

export interface SyncStatus {
  state: SyncState;
  pending: number; // reviews + writes + lesson rows waiting
  lastSync: string | null;
  error?: string;
}

export interface SyncDeps {
  db: ValyrioDB;
  api: ApiClient;
  isOnline?: () => boolean;
  /**
   * Called after a pull was applied. `changed` = the cards the pull actually replaced on this
   * device (the app announces them as `cardsChanged`, lib/cardEvents.ts).
   */
  onPulled?: (pull: SyncPull, changed: CardRow[]) => void;
  /** Called after dictionary changes were merged. */
  onDictionary?: (overlay: DictOverlay) => void;
  onStatus?: (s: SyncStatus) => void;
  /** Called when the server refused a queued write for good (the screen that made it shows why). */
  onRejected?: (w: OutboxWrite) => void;
  /** The bundle's generatedAt, the first `since` for dictionary changes. */
  bundleGeneratedAt?: () => string | null;
  /** Clock (tests). */
  now?: () => number;
  /**
   * Called on each online run right before the outbox is sent: a write it queues goes in this same
   * sync (phone health, health/report.ts queueHealthReport). A failure here never stops the sync.
   */
  beforeFlush?: () => Promise<unknown>;
}

/** Reviews / dirty cards per POST /api/sync (the server takes at most 2,000 of each). */
export const PUSH_CHUNK = 500;
/** A page becoming visible syncs only when the last good sync is older than this, or something waits. */
export const VISIBLE_MIN_INTERVAL_MS = 60_000;
/** Without a dictVersion from the server (older API), the dictionary feed is read at most this often. */
export const DICT_FALLBACK_INTERVAL_MS = 10 * 60_000;
/** Safety bound on loops over chunks and pages. */
const MAX_ROUNDS = 50;
/** Retry delays after a failed sync while the browser says it is online (captive Wi-Fi, dead zone). */
export const RETRY_BACKOFF_MS = [5_000, 15_000, 60_000, 300_000] as const;
/** A queued write that keeps failing with a retryable error is parked after this many attempts. */
export const MAX_WRITE_ATTEMPTS = 8;
/**
 * 409 reasons a retry cannot fix (the user must act). Any other 409 (a concurrent change) is
 * retried once after the next pull before it is parked.
 */
const FINAL_CONFLICTS = new Set(['letter_changed', 'flag_closed', 'already_decided', 'duplicate_headword', 'duplicate_own_word']);

export const uid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Store a graded card and its review in one transaction (local-first). */
export async function recordReview(db: ValyrioDB, card: CardRow, review: ReviewRow): Promise<void> {
  const tx = db.transaction(['cards', 'reviewOutbox'], 'readwrite');
  const rec: CardRecord = { ...card, key: cardKey(card.type, card.itemId), dirty: 1 };
  await Promise.all([tx.objectStore('cards').put(rec), tx.objectStore('reviewOutbox').put(review), tx.done]);
}

export async function recordLessonProgress(db: ValyrioDB, row: LessonProgressRow): Promise<void> {
  await db.put('lessonOutbox', row);
}

/** A write queued before 2026-10-13 under the old partner pass route goes to its new path (api/src/routes/acting.ts). */
const outboxPath = (path: string): string => (path === '/api/partner-pass/reviews' ? '/api/acting/reviews' : path);

/** Queue any other write (flag, settings, pace proposal, …). Returns the client id. */
export async function enqueueWrite(
  db: ValyrioDB,
  w: Pick<OutboxWrite, 'method' | 'path' | 'body' | 'kind' | 'undo' | 'as'> & { id?: string },
): Promise<string> {
  const id = w.id ?? uid();
  await db.add('writeOutbox', {
    id,
    method: w.method,
    path: w.path,
    body: w.body,
    kind: w.kind,
    createdAt: new Date().toISOString(),
    attempts: 0,
    ...(w.undo !== undefined ? { undo: w.undo } : {}),
    // Yksi puhelin: the partner's write, made on this phone (together/onephone/)
    ...(w.as ? { as: w.as } : {}),
  });
  return id;
}

export async function pendingCount(db: ValyrioDB): Promise<number> {
  const [r, w, l, v] = await Promise.all([db.count('reviewOutbox'), db.count('writeOutbox'), db.count('lessonOutbox'), kvGet(db, 'reviewVoids')]);
  return r + w + l + (v?.length ?? 0);
}

/**
 * The server decides between versions of a card (more reviews win, api/src/routes/sync.ts
 * CARD_WINS) and returns its decision for every card pushed, so a pulled card replaces any local
 * copy that is not waiting to be pushed. A dirty local card (graded while the request was out)
 * stays: it goes up with the next push and the server decides then.
 */
export function remoteWins(local: CardRecord | undefined, _remote: CardRow): boolean {
  return !local || !local.dirty;
}

const dictEmpty = (c: Awaited<ReturnType<ApiClient['dictionaryChanges']>>) =>
  !c.lemmas.length &&
  !c.glosses.length &&
  !c.examples.length &&
  !c.forms.length &&
  !c.removedLemmaIds.length &&
  !c.removedGlossIds?.length &&
  !c.removedExampleIds?.length;

export class SyncEngine {
  private running: Promise<SyncStatus> | null = null;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private status: SyncStatus = { state: 'idle', pending: 0, lastSync: null };
  /** When the last sync finished without an error (ms); throttles the visibility trigger. */
  private lastOkAt = 0;
  /** When the dictionary feed was last read (fallback throttle for servers without dictVersion). */
  private lastDictAt = 0;
  /** Consecutive failed runs (drives the retry backoff). */
  private failures = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay: number | null = null;

  constructor(private readonly deps: SyncDeps) {}

  private now(): number {
    return this.deps.now ? this.deps.now() : Date.now();
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  /** The delay of the scheduled retry after a failure (null when none is scheduled). */
  get retryInMs(): number | null {
    return this.retryTimer ? this.retryDelay : null;
  }

  private setStatus(s: Partial<SyncStatus>) {
    this.status = { ...this.status, ...s };
    if (s.state !== 'error' && s.state !== undefined) delete this.status.error;
    this.deps.onStatus?.(this.status);
  }

  /** Fire-and-forget, debounced. Safe to call from anywhere, any number of times. */
  request(delayMs = 300): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.syncNow().catch(() => undefined);
    }, delayMs);
  }

  /** The visibility trigger: only when something waits, or the last good sync is a minute old. */
  shouldSyncOnVisible(): boolean {
    return this.status.pending > 0 || this.status.state !== 'idle' || this.now() - this.lastOkAt >= VISIBLE_MIN_INTERVAL_MS;
  }

  /** Run one full sync now. Concurrent calls share the run; a call during a run schedules one more. */
  syncNow(): Promise<SyncStatus> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = this.run().finally(() => {
      this.running = null;
      if (this.again) {
        this.again = false;
        this.request(0);
      }
    });
    return this.running;
  }

  private async run(): Promise<SyncStatus> {
    const { db } = this.deps;
    const lastSync = (await kvGet(db, 'lastSync')) ?? null;
    this.setStatus({ lastSync, pending: await pendingCount(db) });
    if (this.deps.isOnline && !this.deps.isOnline()) {
      this.setStatus({ state: 'offline' });
      return this.status;
    }
    this.setStatus({ state: 'syncing' });
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    try {
      // Queued writes first (so the pull already shows their effect), but a write the server
      // cannot take right now never holds back the study rows: the push runs regardless.
      let writeError: unknown = null;
      let deferred = false;
      try {
        await this.deps.beforeFlush?.();
      } catch {
        /* phone health is best effort */
      }
      try {
        deferred = await this.flushWrites();
      } catch (err) {
        if (err instanceof ApiError && err.auth) throw err;
        writeError = err;
      }
      const pull = await this.pushPull();
      try {
        await this.pullDictionary(pull.dictVersion);
      } catch (err) {
        // Dictionary changes are an overlay; study works without them. Only a lost session matters.
        if (err instanceof ApiError && err.auth) throw err;
      }
      // A write that met a concurrent change is retried once, now that the pull has the new state.
      if (deferred) this.again = true;
      if (writeError) throw writeError;
      this.lastOkAt = this.now();
      this.failures = 0;
      this.setStatus({ state: 'idle', pending: await pendingCount(db), lastSync: (await kvGet(db, 'lastSync')) ?? null });
    } catch (err) {
      const e = err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err));
      const state: SyncState = e.auth ? 'auth' : e.status === 0 ? 'offline' : 'error';
      this.status = { ...this.status, state, error: e.message, pending: await pendingCount(db) };
      this.deps.onStatus?.(this.status);
      if (!e.auth) this.scheduleRetry();
    }
    return this.status;
  }

  /**
   * After a failure while the browser claims to be online (no `online` event will come), try
   * again with backoff: 5 s, 15 s, 1 min, then every 5 min. Only while something waits to be
   * sent; reset by any good sync.
   */
  private scheduleRetry(): void {
    if (this.deps.isOnline && !this.deps.isOnline()) return;
    if (this.status.pending === 0 || this.retryTimer) return;
    const delay = RETRY_BACKOFF_MS[Math.min(this.failures, RETRY_BACKOFF_MS.length - 1)]!;
    this.failures++;
    this.retryDelay = delay;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.syncNow().catch(() => undefined);
    }, delay);
  }

  /**
   * Send queued writes in order, each with its outbox id as Idempotency-Key (a resend after a
   * lost response is answered from the server's record instead of running twice). Stops at the
   * first retryable failure (keeps order). Returns true when a write was put back for one retry.
   */
  async flushWrites(): Promise<boolean> {
    const { db, api } = this.deps;
    const writes = await db.getAll('writeOutbox');
    let deferred = false;
    for (const w of writes) {
      try {
        // Yksi puhelin: the partner's write goes as theirs (a row queued with the old partner pass too: its `as`)
        await api.request(w.method, outboxPath(w.path), w.body, { idempotencyKey: w.id, ...(w.as ? { actingAs: w.as } : {}) });
        await db.delete('writeOutbox', w.seq!);
      } catch (err) {
        if (err instanceof ApiError && !err.retryable && !err.auth) {
          if (err.status === 409 && w.attempts === 0 && !FINAL_CONFLICTS.has(err.reason ?? '')) {
            await db.put('writeOutbox', { ...w, attempts: 1, lastError: `${err.status} ${err.reason ?? err.code}` });
            deferred = true;
            continue;
          }
          // Rejected for good (validation, a change that no longer fits …): park it where the
          // screen that made it can explain why (lib/rejected.ts), with the server's reason.
          const dead = (await kvGet(db, 'deadWrites')) ?? [];
          const parked: OutboxWrite = {
            ...w,
            attempts: w.attempts + 1,
            lastError: `${err.status} ${err.reason ?? err.code}`,
            status: err.status,
            message: err.message,
            rejectedAt: new Date(this.now()).toISOString(),
            ...(err.reason ? { reason: err.reason } : {}),
          };
          dead.push(parked);
          const tx = db.transaction(['kv', 'writeOutbox'], 'readwrite');
          await Promise.all([tx.objectStore('kv').put(dead.slice(-50), 'deadWrites'), tx.objectStore('writeOutbox').delete(w.seq!), tx.done]);
          this.deps.onRejected?.(parked);
          continue;
        }
        if (err instanceof ApiError && err.retryable && err.status !== 0 && w.attempts + 1 >= MAX_WRITE_ATTEMPTS) {
          // The server keeps failing on this one write (5xx/429 again and again): park it so the
          // writes after it can go.
          const dead = (await kvGet(db, 'deadWrites')) ?? [];
          const parked: OutboxWrite = {
            ...w,
            attempts: w.attempts + 1,
            lastError: `${err.status} ${err.code}`,
            status: err.status,
            message: err.message,
            rejectedAt: new Date(this.now()).toISOString(),
          };
          dead.push(parked);
          const tx = db.transaction(['kv', 'writeOutbox'], 'readwrite');
          await Promise.all([tx.objectStore('kv').put(dead.slice(-50), 'deadWrites'), tx.objectStore('writeOutbox').delete(w.seq!), tx.done]);
          this.deps.onRejected?.(parked);
          continue;
        }
        await db.put('writeOutbox', { ...w, attempts: w.attempts + 1, lastError: String(err) });
        throw err;
      }
    }
    return deferred;
  }

  /**
   * POST /api/sync with the review outbox, dirty cards and lesson rows (in chunks of PUSH_CHUNK,
   * looping until the outboxes are empty); apply each pull. A paged pull (a full pull of a big
   * account) is completed with GET /api/sync?cursor= before it is applied.
   */
  async pushPull(): Promise<SyncPull> {
    const { db, api } = this.deps;
    let last: SyncPull | null = null;
    for (let round = 0; round < MAX_ROUNDS; round++) {
      const [reviews, dirty, lessons, since, lettersSeenAt, allVoids] = await Promise.all([
        db.getAll('reviewOutbox', undefined, PUSH_CHUNK),
        db.getAllFromIndex('cards', 'dirty', 1, PUSH_CHUNK),
        db.getAll('lessonOutbox'),
        kvGet(db, 'lastSync'),
        kvGet(db, 'lettersSeenAt'),
        kvGet(db, 'reviewVoids'),
      ]);
      // Undone reviews the server may have ("Kumoa" after a sync): it deletes them (idempotent).
      const voids = (allVoids ?? []).slice(0, PUSH_CHUNK);
      const push: SyncPush = {
        since: since ?? null,
        reviews,
        cards: dirty.map(toCardRow),
        lessonProgress: lessons,
        ...(lettersSeenAt ? { lettersSeenAt } : {}),
        ...(voids.length ? { voidReviews: voids } : {}),
      };
      let pull = await api.sync(push);
      if (pull.more && pull.cursor) {
        const cards = [...pull.cards];
        let cursor: string | undefined = pull.cursor;
        for (let page = 0; cursor && page < MAX_ROUNDS * 4; page++) {
          const next = await api.syncPage(push.since, cursor, lettersSeenAt ?? null);
          cards.push(...next.cards);
          cursor = next.more ? next.cursor : undefined;
        }
        // The first page's `now` stays the next `since`: anything changed while paging is newer.
        pull = { ...pull, cards, more: false };
        delete pull.cursor;
      }
      const changed = await this.applyPull(push, pull);
      this.deps.onPulled?.(pull, changed);
      last = pull;
      if (reviews.length < PUSH_CHUNK && dirty.length < PUSH_CHUNK && voids.length < PUSH_CHUNK) break;
    }
    return last!;
  }

  /**
   * Clear what was pushed (only if unchanged since) and merge what came back. Returns the cards
   * that replaced a different local copy (or arrived new).
   */
  async applyPull(push: SyncPush, pull: SyncPull): Promise<CardRow[]> {
    const { db } = this.deps;
    const tx = db.transaction(['cards', 'reviewOutbox', 'lessonOutbox', 'kv'], 'readwrite');
    const cards = tx.objectStore('cards');
    const outbox = tx.objectStore('reviewOutbox');
    const lessonsOut = tx.objectStore('lessonOutbox');
    const kv = tx.objectStore('kv');
    const ops: Promise<unknown>[] = [];

    for (const r of push.reviews) ops.push(outbox.delete(r.id));
    for (const pushed of push.cards) {
      const key = cardKey(pushed.type, pushed.itemId);
      ops.push(
        cards.get(key).then((cur) => {
          if (cur && cur.dirty && cur.updatedAt === pushed.updatedAt) return cards.put({ ...cur, dirty: 0 });
          return undefined;
        }),
      );
    }
    for (const l of push.lessonProgress) {
      ops.push(lessonsOut.get(l.lessonId).then((cur) => (cur && JSON.stringify(cur) === JSON.stringify(l) ? lessonsOut.delete(l.lessonId) : undefined)));
    }
    if (push.voidReviews?.length) {
      // the server has applied these voids; one undone while the request was out stays queued
      const sent = new Set(push.voidReviews.map((v) => v.id));
      ops.push(
        (kv.get('reviewVoids') as Promise<ReviewVoid[] | undefined>).then(async (cur) => {
          const left = (cur ?? []).filter((v) => !sent.has(v.id));
          if (left.length) await kv.put(left, 'reviewVoids');
          else await kv.delete('reviewVoids');
        }),
      );
    }
    await Promise.all(ops);

    const merges: Promise<unknown>[] = [];
    const changed: CardRow[] = [];
    for (const remote of pull.cards) {
      const key = cardKey(remote.type, remote.itemId);
      merges.push(
        cards.get(key).then((local) => {
          if (!remoteWins(local, remote)) return undefined;
          if (!local || local.updatedAt !== remote.updatedAt || JSON.stringify(local.state) !== JSON.stringify(remote.state)) changed.push(remote);
          return cards.put({ ...remote, key, dirty: 0 });
        }),
      );
    }
    merges.push(kv.put(pull.lessonProgress, 'lessonProgress'));
    merges.push(kv.put(pull.shared, 'shared'));
    merges.push(kv.put(pull.now, 'sharedAt'));
    merges.push(kv.put(pull.now, 'lastSync'));
    if (pull.inbox) merges.push(kv.put(pull.inbox, 'inbox'));
    await Promise.all(merges);
    await tx.done;
    return changed;
  }

  /**
   * GET /api/dictionary/changes since the last pull (or the bundle's generatedAt), only when the
   * server's dictVersion differs from the last complete pull; follows `more` pages.
   */
  async pullDictionary(version?: string): Promise<void> {
    const { db, api } = this.deps;
    const overlay = await kvGet(db, 'dictOverlay');
    let since = overlay?.since ?? this.deps.bundleGeneratedAt?.() ?? null;
    if (!since) return;
    if (version !== undefined) {
      if (overlay && version === (await kvGet(db, 'dictVersion'))) return;
    } else if (this.lastDictAt && this.now() - this.lastDictAt < DICT_FALLBACK_INTERVAL_MS) {
      return;
    }
    let next = overlay ?? emptyOverlay(since);
    let changed = false;
    let complete = false;
    for (let round = 0; round < MAX_ROUNDS && !complete; round++) {
      const changes = await api.dictionaryChanges(since);
      if (!dictEmpty(changes)) changed = true;
      next = mergeChanges(next, changes);
      await kvSet(db, 'dictOverlay', next);
      since = changes.now;
      complete = !changes.more;
    }
    this.lastDictAt = this.now();
    if (version !== undefined && complete) await kvSet(db, 'dictVersion', version);
    if (changed) this.deps.onDictionary?.(next);
  }

  /** Register the triggers. Returns a cleanup function. */
  start(): () => void {
    const onOnline = () => this.request(0);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && this.shouldSyncOnVisible()) this.request(0);
      // Leaving the app (swiped away, phone locked): send what waits while the page still runs.
      else if (document.visibilityState === 'hidden' && this.status.pending > 0) void this.syncNow().catch(() => undefined);
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    this.request(0);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }
}
